import React, { useState, useCallback, useEffect } from 'react';
import { Mail, AlertCircle, CheckCircle } from 'lucide-react';
import clsx from 'clsx';
import { useApp } from '../contexts/AppContext';

interface EmailInputProps {
  onEmailChange: (email: string, isValid: boolean) => void;
  initialEmail?: string;
  required?: boolean;
  placeholder?: string;
}

/**
 * EmailInput Component - User email collection with validation
 * 
 * Features:
 * - Real-time email validation
 * - Visual feedback for validation state
 * - Accessible design with proper ARIA labels
 * - Persists email in localStorage
 * - Integration with job submission workflow
 */
export const EmailInput = React.forwardRef<HTMLInputElement, EmailInputProps>((
  {
    onEmailChange,
    initialEmail = '',
    required = true,
    placeholder = 'Enter your email to receive organized images'
  },
  ref
) => {
  const { state, stopEmailBounce } = useApp();
  const { shouldBounceEmail } = state;
  
  const [email, setEmail] = useState(initialEmail || '');

  const [isTouched, setIsTouched] = useState(false);
  const [isValid, setIsValid] = useState(false);

  // Email validation regex
  const validateEmail = useCallback((email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }, []);

  // Sync local email state when parent sets email via context (e.g., after file upload)
  useEffect(() => {
    const contextEmail = state.email;
    if (contextEmail && contextEmail !== email && state.isEmailValid) {
      console.log('📧 EmailInput syncing with context email:', contextEmail);
      setEmail(contextEmail);
      setIsValid(true);
      setIsTouched(true);
    }
  }, [state.email, state.isEmailValid, email]);

  // Handle email input changes (local state only)
  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    if (!isTouched) {
      setIsTouched(true);
    }

    const valid = validateEmail(newEmail);
    setIsValid(valid);

    // Stop bounce animation when user starts typing
    if (shouldBounceEmail) {
      stopEmailBounce();
    }
  }, [isTouched, validateEmail, shouldBounceEmail, stopEmailBounce]);

  // Handle blur to mark field as touched and notify parent
  const handleBlur = useCallback(() => {
    setIsTouched(true);

    // Only notify parent component on blur (when user moves focus away)
    const valid = validateEmail(email);
    onEmailChange(email.trim(), valid);
  }, [email, validateEmail, onEmailChange]);

  // Handle focus to scroll input to center of viewport (desktop & mobile best practice)
  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    // Use setTimeout to ensure the keyboard is open on mobile before scrolling
    setTimeout(() => {
      e.target.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 100);
  }, []);

  // Handle Enter key to trigger submission when email is valid
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isValid) {
      e.preventDefault();
      // Notify parent of valid email (ensures state is synced)
      onEmailChange(email.trim(), true);
      // Dispatch custom event for Enter key submission
      // eslint-disable-next-line no-undef
      window.dispatchEvent(new CustomEvent('emailEnterSubmit'));
    }
  }, [email, isValid, onEmailChange]);

  // Visual states
  const showError = isTouched && email && !isValid;
  const showSuccess = isTouched && email && isValid;

  // Auto-stop bounce animation after 10 seconds
  useEffect(() => {
    if (shouldBounceEmail) {
      const timeout = setTimeout(() => {
        stopEmailBounce();
      }, 10000); // Stop bouncing after 10 seconds
      
      return () => clearTimeout(timeout);
    }
  }, [shouldBounceEmail, stopEmailBounce]);

  return (
    <div className="w-full">
      <div className="p-4 border-b border-gray-200 text-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center justify-center">
          <Mail className="w-5 h-5 mr-2" />
          2) Email Address For Results
        </h2>
      </div>
      
      <label
        htmlFor="user-email"
        className={clsx(
          "block text-base font-medium text-gray-700 mb-2 transition-all duration-300",
          shouldBounceEmail && "animate-bounce text-purple-600"
        )}
      >
        {shouldBounceEmail ? (
          <span className="font-semibold text-lg">📧 Enter email to send results to →</span>
        ) : (
          <>Email Address {required && <span className="text-red-500">*</span>}</>
        )}
      </label>
      
      <div className="relative">
        <input
          ref={ref}
          id="user-email"
          type="email"
          value={email}
          onChange={handleEmailChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          className={clsx(
            'input-enhanced placeholder-gray-400',
            showError && 'border-red-300 focus:border-red-500 focus:ring-red-200',
            showSuccess && 'border-green-300 focus:border-green-500 focus:ring-green-200',
            !showError && !showSuccess && 'border-gray-300 focus:border-purple-500 focus:ring-purple-200'
          )}
          aria-describedby={showError ? 'email-error' : showSuccess ? 'email-success' : undefined}
          aria-invalid={showError ? 'true' : 'false'}
        />
        
        {/* Validation Icons */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {showError && (
            <AlertCircle className="w-5 h-5 text-red-500" />
          )}
          {showSuccess && (
            <CheckCircle className="w-5 h-5 text-green-500" />
          )}
        </div>
      </div>

      {/* Validation Messages */}
      {showError && (
        <p id="email-error" className="mt-1 text-sm text-red-600 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          Please enter a valid email address
        </p>
      )}
      
      {showSuccess && (
        <p id="email-success" className="mt-1 text-sm text-green-600 flex items-center">
          <CheckCircle className="w-4 h-4 mr-1" />
          Valid email address
        </p>
      )}

      {/* Help Text */}
      <p className="mt-2 text-xs text-black">
        We&apos;ll send your organized images to this email address. Your email is not saved permanently.
      </p>
    </div>
  );
});

EmailInput.displayName = 'EmailInput';

export default EmailInput;